/* import __COLOCATED_TEMPLATE__ from './qa-status.hbs'; */
/* RESPONSIBLE TEAM: team-rd-services */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { cached } from 'tracked-toolbox';
import type IntlService from 'ember-intl/services/intl';
import type Snackbar from 'embercom/services/snackbar';
import type Session from 'embercom/services/session';
import type ConversationTableEntry from 'embercom/objects/inbox/conversation-table-entry';
import { putRequest } from 'embercom/lib/inbox/requests';
import QaStatusSelectItem from './qa-status-select-item';

export enum QaStatusValue {
  Unreviewed = 'unreviewed',
  NeedsFix = 'needs_fix',
  WontFix = 'wont_fix',
  FixComplete = 'fix_complete',
  PassedReview = 'passed_review',
}

interface Signature {
  Args: {
    conversationEntry: ConversationTableEntry;
    statusOverride?: string;
  };
}

const STATUS_DOT_COLORS: Record<QaStatusValue, string> = {
  [QaStatusValue.Unreviewed]: 'bg-neutral-container-emphasis',
  [QaStatusValue.NeedsFix]: 'bg-notice-container-emphasis',
  [QaStatusValue.WontFix]: 'bg-neutral-border-emphasis',
  [QaStatusValue.FixComplete]: 'bg-success-border',
  [QaStatusValue.PassedReview]: 'bg-success-border',
};

interface SelectItem {
  text: string;
  value: QaStatusValue;
  component: typeof QaStatusSelectItem;
  componentAttrs: { dotColorClass: string };
}

export default class QaStatus extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare snackbar: Snackbar;
  @service declare session: Session;

  @cached
  get qaStatusAttribute() {
    return this.args.conversationEntry.getAttributeForDescriptor('qa_status');
  }

  get status(): QaStatusValue | undefined {
    if (this.args.statusOverride) {
      return this.args.statusOverride as QaStatusValue;
    }
    return this.qaStatusAttribute?.value as QaStatusValue | undefined;
  }

  get displayText(): string {
    let status = this.status;

    if (!status) {
      return this.intl.t('inbox.conversations-table.columns.qaStatusValues.unreviewed');
    }

    switch (status) {
      case QaStatusValue.NeedsFix:
        return this.intl.t('inbox.conversations-table.columns.qaStatusValues.needs_fix');
      case QaStatusValue.WontFix:
        return this.intl.t('inbox.conversations-table.columns.qaStatusValues.wont_fix');
      case QaStatusValue.FixComplete:
        return this.intl.t('inbox.conversations-table.columns.qaStatusValues.fix_complete');
      case QaStatusValue.PassedReview:
        return this.intl.t('inbox.conversations-table.columns.qaStatusValues.passed_review');
      default:
        return this.intl.t('inbox.conversations-table.columns.qaStatusValues.unreviewed');
    }
  }

  get dotColorClass(): string {
    return STATUS_DOT_COLORS[this.status ?? QaStatusValue.Unreviewed];
  }

  get colorClasses(): string {
    return 'border border-neutral-border text-default';
  }

  get selectItems(): SelectItem[] {
    let component = QaStatusSelectItem;

    return [
      {
        value: QaStatusValue.Unreviewed,
        text: this.intl.t('inbox.conversations-table.columns.qaStatusValues.unreviewed'),
        component,
        componentAttrs: { dotColorClass: STATUS_DOT_COLORS[QaStatusValue.Unreviewed] },
      },
      {
        value: QaStatusValue.NeedsFix,
        text: this.intl.t('inbox.conversations-table.columns.qaStatusValues.needs_fix'),
        component,
        componentAttrs: { dotColorClass: STATUS_DOT_COLORS[QaStatusValue.NeedsFix] },
      },
      {
        value: QaStatusValue.WontFix,
        text: this.intl.t('inbox.conversations-table.columns.qaStatusValues.wont_fix'),
        component,
        componentAttrs: { dotColorClass: STATUS_DOT_COLORS[QaStatusValue.WontFix] },
      },
      {
        value: QaStatusValue.FixComplete,
        text: this.intl.t('inbox.conversations-table.columns.qaStatusValues.fix_complete'),
        component,
        componentAttrs: { dotColorClass: STATUS_DOT_COLORS[QaStatusValue.FixComplete] },
      },
      {
        value: QaStatusValue.PassedReview,
        text: this.intl.t('inbox.conversations-table.columns.qaStatusValues.passed_review'),
        component,
        componentAttrs: { dotColorClass: STATUS_DOT_COLORS[QaStatusValue.PassedReview] },
      },
    ];
  }

  get selectedValue(): QaStatusValue {
    return this.status ?? QaStatusValue.Unreviewed;
  }

  @action
  async handleSelectItem(selectedValue: QaStatusValue): Promise<void> {
    let conversationId = this.args.conversationEntry.id;
    let appId = this.session.workspace.id;
    let attr = this.qaStatusAttribute;
    let previousValue = attr?.value;

    try {
      if (attr) {
        attr.value = selectedValue;
      }

      await putRequest(
        `/ember/conversations/${conversationId}/conversation_attributes/qa_status?app_id=${appId}`,
        {
          value: selectedValue === QaStatusValue.Unreviewed ? null : selectedValue,
        },
      );
    } catch (e: unknown) {
      if (attr) {
        attr.value = previousValue;
      }
      this.snackbar.notifyError(this.intl.t('inbox.conversations-table.errors.qa-status-update'));
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationsTable::TableCells::QaStatus': typeof QaStatus;
    'inbox2/conversations-table/table-cells/qa-status': typeof QaStatus;
  }
}
